import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';

@Component({
  selector: 'app-privacy-policy',
  standalone: true,
  imports: [CommonModule, RouterModule, CardModule, ButtonModule],
  template: `
    <div class="min-h-screen bg-surface-50 dark:bg-surface-900 p-6 flex justify-center">
      <div class="w-full max-w-3xl">
        <p-card header="Privacy Policy — Torna Customer App">
          <p class="text-sm text-surface-600 dark:text-surface-300 mb-4">
            Effective date: {{ effectiveDate }} · Global Foods Trading (GFT)
          </p>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Overview</h3>
            <p>
              The Torna Customer mobile app is a B2B portal for registered GFT customers.
              This policy describes what information we collect, how we use it, and your choices.
            </p>
          </section>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Information we collect</h3>
            <ul class="list-disc pl-6 space-y-1">
              <li><strong>Account:</strong> email address, display name, customer and company identifiers (via AWS Cognito and GFT portal APIs).</li>
              <li><strong>Orders &amp; invoices:</strong> order history, line details, and invoice PDFs from the Order Manager API.</li>
              <li><strong>Disputes:</strong> optional photos or documents you attach when raising a delivery dispute (up to 10 MB, validated file types).</li>
              <li><strong>Device storage:</strong> authentication tokens stored in encrypted secure storage on your device only.</li>
              <li><strong>Crash diagnostics (optional):</strong> if enabled in production builds, anonymized crash/stack data via Sentry — no intentional collection of PII in crash reports.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">How we use information</h3>
            <ul class="list-disc pl-6 space-y-1">
              <li>Authenticate you and provide access to your orders, invoices, and disputes.</li>
              <li>Process dispute evidence you submit.</li>
              <li>Improve app stability (crash reporting, when enabled).</li>
            </ul>
            <p class="mt-2">We do <strong>not</strong> sell your personal data or use it for third-party advertising.</p>
          </section>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Sharing</h3>
            <p>
              Data is processed by GFT systems (Order Manager API, Cognito authentication) and service providers
              that host those systems. We do not share data with ad networks.
            </p>
          </section>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Security</h3>
            <p>
              Data is transmitted over HTTPS. Authentication tokens on your device are stored using platform secure storage.
              Sign out to clear local session data from the app.
            </p>
          </section>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Permissions (Android)</h3>
            <ul class="list-disc pl-6 space-y-1">
              <li><strong>Internet</strong> — required for API access.</li>
              <li><strong>Camera</strong> — optional; only when you choose to capture dispute photos.</li>
            </ul>
          </section>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Account deletion</h3>
            <p>
              In the app: <strong>Account → Security → Delete account</strong> opens an email to
              <a href="mailto:support&#64;GFT.com">support&#64;GFT.com</a>.
              We aim to process verified deletion requests within 30 days. Order and invoice records may be retained
              where required by law or your commercial agreement with GFT.
            </p>
          </section>

          <section class="mb-4">
            <h3 class="text-lg font-semibold mb-2">Contact</h3>
            <p>
              Questions: <a href="mailto:support&#64;GFT.com">support&#64;GFT.com</a>
            </p>
          </section>

          <p-button label="Back to login" icon="pi pi-arrow-left" routerLink="/login" [text]="true" />
        </p-card>
      </div>
    </div>
  `,
})
export class PrivacyPolicyComponent {
  readonly effectiveDate = 'June 2026';
}
